import { createInstance, type i18n, type Resource } from "i18next"
import type {} from "react-i18next"

import type { Storage } from "../storage/storage"
import { createFormatting } from "./formatters"
import { frameworkLocalizationResources } from "./framework-resources"
import { copyTranslationCatalog, type LocalizationResources, type TranslationCatalog } from "./localization-resources"

export interface LocalizationSettings {
  readonly culture: string
  readonly uiCulture: string
  readonly timeZone: string
}

export interface LocalizationSnapshot extends LocalizationSettings {
  readonly status: "loading" | "ready" | "failed"
  readonly error?: unknown
}

export interface LocalizationOptions {
  readonly culture?: string
  readonly uiCulture?: string
  readonly timeZone?: string
  readonly fallbackUICulture?: string
  /** Constrains both formatting culture and UI culture. Language-only values map to the first matching supported culture. */
  readonly supportedCultures?: readonly string[]
  readonly resources?: readonly LocalizationResources[]
  readonly storage?: Storage
  readonly storageKey?: string
  /** Persistence is best-effort; a storage failure must not disable language selection. */
  readonly onPersistenceError?: (error: unknown) => void
}

export interface Localization {
  /** Use the standard translation APIs. Change culture through setCulture, not changeLanguage. */
  readonly i18n: i18n
  readonly snapshot: LocalizationSnapshot
  readonly ready: Promise<void>
  subscribe(listener: () => void): () => void
  /** Changes formatting and UI culture together for the common application-language case. */
  setCulture(culture: string): Promise<void>
  /** Changes independent localization settings without overloading the meaning of culture. */
  setSettings(settings: Partial<LocalizationSettings>): Promise<void>
}

/** Deterministic standalone component defaults; browser detection happens only at app setup. */
export const defaultLocalizationSettings: LocalizationSettings = Object.freeze({
  culture: "en-GB",
  uiCulture: "en",
  timeZone: "UTC",
})

export function createLocalization(options: LocalizationOptions = {}): Localization {
  const supported = options.supportedCultures?.map(canonicalCulture)
  if (supported?.length === 0) throw new Error("supportedCultures must not be empty.")
  const fallback = canonicalCulture(options.fallbackUICulture ?? "en")
  const definitions = [frameworkLocalizationResources, ...(options.resources ?? [])]
  const namespaces = new Set<string>()
  for (const definition of definitions) {
    if (namespaces.has(definition.namespace)) {
      throw new Error(`Duplicate localization namespace: ${definition.namespace}.`)
    }
    namespaces.add(definition.namespace)
  }
  const baseline = normalizeSettings(
    {
      culture: options.culture ?? detectCulture(supported),
      uiCulture: options.uiCulture ?? options.culture ?? detectCulture(supported),
      timeZone: options.timeZone ?? new Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    supported
  )
  const storageKey = options.storageKey ?? "localization"
  let stored: string | null | Promise<string | null> = null
  try {
    stored = options.storage?.getItem(storageKey) ?? null
  } catch (error) {
    options.onPersistenceError?.(error)
  }
  const restored = (text: string | null) => restoreSettings(text, baseline, options, supported)
  const initial = typeof stored === "string" || stored === null ? restored(stored) : baseline
  let snapshot: LocalizationSnapshot = Object.freeze({ ...initial, status: "loading" })
  let requested: LocalizationSettings = initial
  let revision = 0
  let persistence: Promise<void> = Promise.resolve()
  const listeners = new Set<() => void>()
  const instance = createInstance()
  const inline: Resource = Object.create(null)
  const loaded = new Set<string>()
  const pending = new Map<string, Promise<void>>()

  for (const definition of definitions) {
    for (const [language, source] of Object.entries(definition.resources)) {
      if (typeof source === "function") continue
      inline[language] ??= Object.create(null)
      inline[language][definition.namespace] = copyTranslationCatalog(source)
      loaded.add(`${language}\0${definition.namespace}`)
    }
  }
  // I18nextProvider supplies this instance explicitly. initReactI18next would also
  // install a module-global default instance, which is inappropriate for a library.
  void instance.init({
    lng: initial.uiCulture,
    fallbackLng: fallback,
    ns: [...namespaces],
    defaultNS: "florisoft-framework",
    resources: inline,
    initAsync: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false, bindI18n: "languageChanged" },
  })

  function publish(settings: LocalizationSettings, status: LocalizationSnapshot["status"], error?: unknown) {
    snapshot = Object.freeze({ ...settings, status, ...(error === undefined ? {} : { error }) })
    for (const listener of listeners) listener()
  }

  function loadResources(uiCulture: string): Promise<void> | undefined {
    const languages = [...new Set([...languageHierarchy(uiCulture), ...languageHierarchy(fallback)])]
    const loads: Promise<void>[] = []
    for (const definition of definitions) {
      for (const language of languages) {
        const source = definition.resources[language]
        const key = `${language}\0${definition.namespace}`
        if (!source || loaded.has(key)) continue
        let load = pending.get(key)
        if (!load) {
          load = Promise.resolve()
            .then(() => (typeof source === "function" ? source() : source))
            .then(
              (catalog: TranslationCatalog) => {
                instance.addResourceBundle(language, definition.namespace, copyTranslationCatalog(catalog), true, true)
                loaded.add(key)
                pending.delete(key)
              },
              (error) => {
                pending.delete(key)
                throw error
              }
            )
          // Validation/addResourceBundle can fail as well as the loader itself.
          load = load.catch((error) => {
            pending.delete(key)
            throw error
          })
          pending.set(key, load)
        }
        loads.push(load)
      }
    }
    return loads.length ? Promise.all(loads).then(() => undefined) : undefined
  }

  function commit(settings: LocalizationSettings) {
    // Catalogs are already loaded and no backend is installed: changeLanguage emits
    // synchronously. Publish the corresponding formatting culture in the same turn.
    snapshot = Object.freeze({ ...settings, status: "ready" })
    void instance.changeLanguage(settings.uiCulture)
    for (const listener of listeners) listener()
  }

  function save(settings: LocalizationSettings): Promise<void> {
    const text = JSON.stringify({ culture: settings.culture, uiCulture: settings.uiCulture })
    persistence = persistence.then(async () => {
      try {
        await options.storage?.setItem(storageKey, text)
      } catch (error) {
        options.onPersistenceError?.(error)
      }
    })
    return persistence
  }

  async function initializeAsync(settings: LocalizationSettings, resources?: Promise<void>) {
    try {
      await (resources ?? loadResources(settings.uiCulture))
      if (revision === 0) {
        requested = settings
        commit(settings)
      }
    } catch (error) {
      if (revision !== 0) return
      publish(settings, "failed", error)
      throw error
    }
  }

  let ready: Promise<void>
  if (stored && typeof stored !== "string") {
    ready = Promise.resolve(stored)
      .catch((error) => {
        options.onPersistenceError?.(error)
        return null
      })
      .then((text) => initializeAsync(restored(text)))
  } else {
    const loading = loadResources(initial.uiCulture)
    if (loading) ready = initializeAsync(initial, loading)
    else {
      commit(initial)
      ready = Promise.resolve()
    }
  }
  // Consumers can await ready or let ApplicationProvider surface initialization
  // failures through its render error boundary.
  void ready.catch(() => undefined)

  let formattingSnapshot: LocalizationSettings | undefined
  let formatting = createFormatting(snapshot)
  function getFormatting() {
    if (formattingSnapshot !== snapshot) {
      formattingSnapshot = snapshot
      formatting = createFormatting(snapshot)
    }
    return formatting
  }
  // Message interpolation follows formatting culture, not the catalog fallback language.
  instance.services.formatter?.add("number", (value, _language, formatOptions) =>
    getFormatting().number(value, formatOptions)
  )
  instance.services.formatter?.add("currency", (value, _language, formatOptions) => {
    if (typeof formatOptions?.currency !== "string") throw new Error("Currency formatting requires a currency code.")
    return getFormatting().currency(value, formatOptions.currency, formatOptions)
  })
  instance.services.formatter?.add("datetime", (value, _language, formatOptions) =>
    getFormatting().dateTime(value, formatOptions)
  )
  instance.services.formatter?.add("dateonly", (value, _language, formatOptions) =>
    getFormatting().dateOnly(value, formatOptions)
  )
  instance.services.formatter?.add("list", (value, _language, formatOptions) =>
    getFormatting().list(value, formatOptions)
  )

  async function setSettings(value: Partial<LocalizationSettings>) {
    const settings = normalizeSettings(
      {
        ...requested,
        ...value,
      },
      supported
    )
    requested = settings
    const request = ++revision
    try {
      await loadResources(settings.uiCulture)
      if (request !== revision) return
      commit(settings)
      await save(settings)
    } catch (error) {
      if (request !== revision) return
      requested = snapshot
      if (snapshot.status !== "ready") publish(snapshot, "failed", error)
      throw error
    }
  }

  return Object.freeze({
    i18n: instance,
    get snapshot() {
      return snapshot
    },
    ready,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    setCulture(culture: string) {
      return setSettings({ culture, uiCulture: culture })
    },
    setSettings,
  })
}

function canonicalCulture(value: string): string {
  const culture = Intl.getCanonicalLocales(value)[0]
  if (!culture) throw new RangeError("Culture must not be empty.")
  return culture
}

function normalizeSettings(value: LocalizationSettings, supported?: readonly string[]): LocalizationSettings {
  const culture = normalizeSupportedCulture(value.culture, supported)
  const uiCulture = normalizeSupportedCulture(value.uiCulture, supported)
  const timeZone = new Intl.DateTimeFormat("en", { timeZone: value.timeZone }).resolvedOptions().timeZone
  return Object.freeze({ culture, uiCulture, timeZone })
}

function normalizeSupportedCulture(value: string, supported?: readonly string[]): string {
  const culture = canonicalCulture(value)
  if (!supported || supported.includes(culture)) return culture
  const match = supported.find((candidate) => sameLanguage(candidate, culture))
  if (!match) throw new RangeError(`Unsupported culture: ${culture}.`)
  return match
}

function sameLanguage(first: string, second: string) {
  const a = new Intl.Locale(first).maximize()
  const b = new Intl.Locale(second).maximize()
  return a.language === b.language && a.script === b.script
}

function detectCulture(supported?: readonly string[]) {
  const preferences = typeof navigator === "undefined" ? [] : navigator.languages
  for (const preference of preferences) {
    try {
      const culture = canonicalCulture(preference)
      if (!supported) return culture
      const match =
        supported.find((candidate) => candidate === culture) ??
        supported.find((candidate) => sameLanguage(candidate, culture))
      if (match) return match
    } catch {
      /* Ignore an invalid host preference, not an invalid explicit option. */
    }
  }
  return supported?.find((candidate) => candidate === "en-GB") ?? supported?.[0] ?? "en-GB"
}

function restoreSettings(
  text: string | null,
  baseline: LocalizationSettings,
  options: LocalizationOptions,
  supported?: readonly string[]
) {
  if (!text) return baseline
  try {
    const stored: unknown = JSON.parse(text)
    if (!stored || typeof stored !== "object") return baseline
    const values = stored as Record<string, unknown>
    return normalizeSettings(
      {
        culture: options.culture ?? (typeof values.culture === "string" ? values.culture : baseline.culture),
        uiCulture:
          options.uiCulture ??
          options.culture ??
          (typeof values.uiCulture === "string" ? values.uiCulture : baseline.uiCulture),
        // Time zone defaults to the current host every startup unless the host explicitly
        // supplies one. A language change must not turn a detected travel/OS zone into a
        // persisted user preference.
        timeZone: options.timeZone ?? baseline.timeZone,
      },
      supported
    )
  } catch {
    return baseline
  }
}

function languageHierarchy(value: string): string[] {
  const locale = new Intl.Locale(value)
  return [
    ...new Set([
      value,
      locale.baseName,
      ...(locale.script ? [`${locale.language}-${locale.script}`] : []),
      locale.language,
    ]),
  ]
}
